// Hooks
import { defineStore } from "pinia";
import { computed, inject, ref, watch } from "vue";

import initWidget from "@/helpers/initWidget";
import { useAnalyticsEventTiModel } from "@/hooks/useAnalytics/useAnalytics";
// Stores
import useStoreResetter from "@/hooks/useStoreResetter/useStoreResetter";

// Types
import type { I_CUSTOM_STYLES, I_INJECTED_TIW, I_TIW } from "./tiw.types";

/**
 * 🆂🆃🅾🆁🅴 [TIW STORE] Самый высокоуровневый STORE приложения
 */
export const useTIWStore = defineStore("TIW", () => {
    // |---------------|--------------------|
    // | INJECTED DATA | APP INITIALIZATION |
    // |---------------|--------------------|

    /**
     * Инициализированный класс TradeInWidget
     */
    const INJECTED_TIW = inject<I_INJECTED_TIW>("widget");

    /**
     * Инициализированный instance текущего приложения
     */
    // const INJECTED_APP = inject("appInstance");

    // |-------|-----------------------------------------------------------------|
    // | STATE | ⓘ См. https://pinia-ru.netlify.app/core-concepts/#setup-stores |
    // |-------|-----------------------------------------------------------------|

    /** Предыдущий шаг формы */
    const PREVIOUS_STEPS = ref<I_TIW["PREVIOUS_STEP"][]>([]);

    /** Текущий шаг формы */
    const CURRENT_STEP = ref<I_TIW["CURRENT_STEP"]>("chooseDevice");

    /** Закрыть / Открыть виджет / true - закрыт */
    const IS_CLOSE = ref<I_TIW["IS_CLOSE"]>(true);

    /** Состояние действия анимации Закрытия */
    const IS_TRANSITION = ref<I_TIW["IS_TRANSITION"]>(false);

    /** Свернуть / Показать виджет / true - свернут  */
    const IS_ROLL = ref<I_TIW["IS_ROLL"]>(false);

    /**  Данные виджета (переводы, доступные локали и т.д.) */
    const WIDGET_DATA = ref<I_TIW["WIDGET_DATA"]>(null);
    const hasDiscountLater = computed(() => WIDGET_DATA.value.data.get_discount_later === "1");

    /**  Загрузка данных виджета */
    const IS_INIT_WIDGET_LOADING = ref<boolean>(true);

    const IS_DISCOUNT_LATER = ref<boolean>(false);

    const AUTH_TOKEN = ref<string>("0c7c7b40-f885-4ad5-9622-563438e43362");

    const CHANNEL = ref<string>("APP");

    const SESSION_TOKEN = ref<string | null>(null);

    const CERTIFICATE_NUMBER = ref<string | null>(null);
    const ADDITIONAL_CERTIFICATE_NUMBERS = ref<string[]>([]);
    function setAdditionalCertificateNumbers(newArr: string[]) {
        ADDITIONAL_CERTIFICATE_NUMBERS.value = newArr;
    }

    const HOLD = ref<boolean>(true);

    // |----------|
    // | COMPUTED |
    // |----------|
    const locationIsUA = computed<boolean>(() => {
        const _location: string = String(WIDGET_DATA.value.location).toLowerCase();

        return _location === "ua";
    });

    const locationIsZA = computed<boolean>(() => {
        const _location: string = String(WIDGET_DATA.value.location).toLowerCase();

        return _location === "za";
    });

    const domainIsComfy = computed(() => {
        return INJECTED_TIW.current_settings.domain === "comfy.ua";
    });

    const phoneMask = computed<string>(() => {
        const _location: string = String(WIDGET_DATA.value.location).toLowerCase();

        if (_location === "ua") {
            return "+38 (___) ___-__-__";
        }

        if (_location === "kz") {
            return "+7 (___) ___-__-__";
        }

        if (_location === "ge") {
            return "+995 ___-__-__-__";
        }

        /*
        if (_location === "pl") {
            return "";
        }
        // */

        if (_location === "md") {
            return "+373 __ ___ ___";
        }

        if (_location === "az") {
            return "+994 __ ___ __ __";
        }

        if (_location === "za") {
            return "+27 (A_) ___-__-__";
        }

        return "+7 (___) ___-__-__";
    });

    const phoneMaskPlaceholder = computed(() => phoneMask.value.replace(/[A-Z]/gi, "_"));

    const passportIssueDateMask = computed<string>(() => {
        return "__.__.____";
    });

    const showHelpCrunchChat = computed<boolean>(
        (): boolean => Number(WIDGET_DATA.value?.data?.chat || 0) === 1,
    );

    const standardizedLocale = computed<string>((): string => {
        let locale: string = INJECTED_TIW.current_settings.locale;

        const standardLocales: Record<string, string> = {
            kz: "kk",
            ua: "uk",
        };

        if (locale in standardLocales) {
            locale = standardLocales[locale];
        }

        return locale;
    });

    const minOrderPrice = computed<number>((): number => {
        return Number(WIDGET_DATA.value.data.min_order || "0");
    });

    const modeIsEmbed = computed<boolean>(
        (): boolean => INJECTED_TIW.current_settings?.mode === "embed",
    );
    const modeIsPopup = computed<boolean>((): boolean => !modeIsEmbed.value);
    const isPickupOnly = computed<boolean>(
        (): boolean => Number(WIDGET_DATA.value.data.pickup_only) === 1,
    );

    /** Выкупаемое устройство */
    const INJECTED_GOOD = ref<I_TIW["INJECTED_GOOD"]>(null);

    const customStyles = computed<I_CUSTOM_STYLES>(() => {
        // for debug
        // https://fonts.googleapis.com/css2?family=Golos+Text:wght@900&display=swap
        // https://cdn.comfy.ua/media/im/fonts/PTRootUI/regular.woff2

        const styles: I_CUSTOM_STYLES = {
            font: {
                family: WIDGET_DATA.value?.data?.font_family ?? "",
                link: "", // WIDGET_DATA.value.data.font_link
                google: "", // WIDGET_DATA.value.data.font_link
                size: WIDGET_DATA.value?.data?.font_size ?? "",
            },
            color: {
                main: WIDGET_DATA.value?.data?.main_color ?? "",
                text: WIDGET_DATA.value?.data?.text_color ?? "",
            },
        };

        const link: string = String(WIDGET_DATA.value?.data?.font_link ?? "");

        if (link) {
            if (/fonts\.googleapis\.com/.test(WIDGET_DATA.value?.data?.font_link ?? "")) {
                styles.font.google = WIDGET_DATA.value?.data?.font_link ?? "";
            } else if (WIDGET_DATA.value?.data?.font_link ?? "") {
                styles.font.link = WIDGET_DATA.value?.data?.font_link ?? "";
                styles.font.family = "TIW-FONT";
            }
        }

        return styles;
    });

    // |---------|
    // | METHODS |
    // |---------|

    /** Проверка на текущий шаг */
    const isCurrentStep = (step: I_TIW["CURRENT_STEP"]): boolean => {
        if (IS_INIT_WIDGET_LOADING.value) {
            return false;
        }
        return CURRENT_STEP.value === step;
    };

    /** Переключить экраны (шаги форм) виджета */
    const switchStep = (toStep: I_TIW["CURRENT_STEP"]) => {
        if (toStep === CURRENT_STEP.value) {
            return;
        }

        // FIXME разберись, почему viewTransition в комопоненте enterImei приводит к перезагрузке страницы
        // if (document.startViewTransition) {
        //     document.startViewTransition(async () => {
        //         changeStep();
        //         await nextTick();
        //     });
        // } else {
        //     changeStep();
        // }

        changeStep();

        function changeStep() {
            if (toStep === "chooseDevice") {
                PREVIOUS_STEPS.value = [];
                CURRENT_STEP.value = toStep;
                return;
            }
            if (PREVIOUS_STEPS.value.includes(toStep)) {
                const index = PREVIOUS_STEPS.value.indexOf(toStep);
                PREVIOUS_STEPS.value.splice(index);
                CURRENT_STEP.value = toStep;
                return;
            }

            PREVIOUS_STEPS.value.push(CURRENT_STEP.value);
            CURRENT_STEP.value = toStep;
        }
    };

    const goToPrevStep = () => {
        if (PREVIOUS_STEPS.value.length === 0) {
            return;
        }

        const prevStep = PREVIOUS_STEPS.value.pop();
        CURRENT_STEP.value = prevStep;
    };

    /** ЗАКРЫТЬ модальное окно виджета */
    const closeWidget = () => {
        if (IS_ROLL.value) {
            return;
        }

        IS_TRANSITION.value = true;
        setTimeout(() => {
            IS_TRANSITION.value = false;
        }, 150);

        // Reset
        HOLD.value = true;
        IS_CLOSE.value = true;
        INJECTED_GOOD.value = null;
        CURRENT_STEP.value = "chooseDevice";
        PREVIOUS_STEPS.value = [];
        IS_DISCOUNT_LATER.value = false;
    };

    const restoreToDefault = () => {
        HOLD.value = true;
        ADDITIONAL_CERTIFICATE_NUMBERS.value = [];
    };

    /** ОТКРЫТЬ модальное окно виджета */
    const openWidget = () => {
        IS_TRANSITION.value = true;
        setTimeout(() => {
            IS_TRANSITION.value = false;
        }, 150);
        IS_CLOSE.value = false;
    };

    /** СВЕРНУТЬ модальное окно виджета */
    const rollDownWidget = () => {
        IS_ROLL.value = true;
    };

    /** РАЗВЕРНУТЬ модальное окно виджета */
    const rollUpWidget = () => {
        IS_ROLL.value = false;
    };

    // |----------|
    // | WATCHERS |
    // |----------|

    if (modeIsPopup.value) {
        watch(IS_CLOSE, () => {
            document.body.style.overflow = !IS_CLOSE.value ? "hidden" : "auto";
        });

        watch(IS_ROLL, () => {
            document.body.style.overflow = IS_ROLL.value ? "auto" : "hidden";
        });
    }

    // |---------------|---------------------------------|
    // | INJECTED DATA | APP ASSIGNMENT EXTERNAL METHODS |
    // |---------------|---------------------------------|

    const initTradeInWithGood = () => {
        console.log("INJECTED_TIW", INJECTED_TIW);
        INJECTED_TIW.tradeInWithGood = (calculatedDeviceData) => {
            useStoreResetter();
            switchStep("chooseDevice");

            const valueAdapter: I_TIW["INJECTED_GOOD"] = {
                article: null,
                panelTitle: null,
                panelPrice: null,
                panelDevicePicture: null,
            };

            valueAdapter.article = calculatedDeviceData?.article.toString();
            valueAdapter.panelTitle = calculatedDeviceData?.name;
            valueAdapter.panelPrice = calculatedDeviceData?.price;
            valueAdapter.panelDevicePicture = calculatedDeviceData?.image;

            INJECTED_GOOD.value = valueAdapter;

            if (IS_INIT_WIDGET_LOADING.value) {
                initWidget().then(() => {
                    IS_INIT_WIDGET_LOADING.value = false;
                    useAnalyticsEventTiModel();
                });
            } else {
                useAnalyticsEventTiModel();
            }

            openWidget();
        };
    };

    return {
        // * 🆂🆃🅾🆁🅴
        PREVIOUS_STEPS,
        CURRENT_STEP,
        IS_ROLL,
        IS_CLOSE,
        IS_TRANSITION,
        WIDGET_DATA,
        hasDiscountLater,
        IS_INIT_WIDGET_LOADING,
        IS_DISCOUNT_LATER,
        AUTH_TOKEN,
        SESSION_TOKEN,
        CERTIFICATE_NUMBER,
        ADDITIONAL_CERTIFICATE_NUMBERS,
        setAdditionalCertificateNumbers,
        HOLD,
        CHANNEL,

        // * 🅲🅾🅼🅿🆄🆃🅴🅳
        locationIsUA,
        locationIsZA,
        phoneMask,
        phoneMaskPlaceholder,
        passportIssueDateMask,
        showHelpCrunchChat,
        standardizedLocale,
        minOrderPrice,
        modeIsEmbed,
        modeIsPopup,
        isPickupOnly,
        customStyles,
        domainIsComfy,

        // * 🅸🅽🅹🅴🅲🆃🅴🅳
        INJECTED_TIW,
        INJECTED_GOOD,

        // * 🅼🅴🆃🅷🅾🅳🆂
        initTradeInWithGood,
        restoreToDefault,
        isCurrentStep,
        switchStep,
        goToPrevStep,
        openWidget,
        closeWidget,
        rollUpWidget,
        rollDownWidget,
    };
});
